/*
 * Copyright (c) 2026 Bluemate B.V.
 *
 * Thin wrapper over IndexedDB for push-notification routing state shared
 * between the page and the service worker on the same origin.
 *
 * Why IDB instead of localStorage / sessionStorage:
 * - SW has no access to either storage. IDB is the only API both sides share.
 * - Survives tab crashes and is visible across all tabs on the origin, so
 *   a push opened in one tab can be picked up by a login completing in
 *   another tab.
 *
 * Stored keys (single object store `state`):
 *   active_user_id   — last user the page told us is logged in (string|null)
 *   pending_nav      — handoff target queued by the SW when openWindow runs:
 *                      { target: string, user_id: string | null, ts: number }
 *                      Consumed once by app.tsx after validateUser() resolves.
 *   known_users      — list of {id, last_seen} ever seen on this device. Used
 *                      by the admin-mismatch dialog so we can show a label
 *                      ("switch to userA?") even when admin context has not
 *                      yet impersonated this user.
 */

const DB_NAME = 'bluemate-push';
const DB_VERSION = 1;
const STORE = 'state';

const KEY_ACTIVE_USER = 'active_user_id';
const KEY_PENDING_NAV = 'pending_nav';
const KEY_KNOWN_USERS = 'known_users';
const KEY_OPTED_IN_USERS = 'opted_in_users';
const KEY_OPTED_IN_PAIRS = 'opted_in_pairs';
// Per-view unread counters: a single map { "<accountId>:<route>": count } so we
// don't need a DB version bump / extra object store. Incremented by the SW push
// handler (survives the app being closed), read by the menus, reset when the
// user opens the target view.
const KEY_UNREAD_COUNTS = 'unread_counts';

export interface PendingNavigation {
    target: string;
    user_id: string | null;
    account_id: string | null;
    ts: number;
}

export interface KnownUser {
    id: string;
    last_seen: number;
    display_name?: string;
}

// Cache the open connection so each idbGet/idbSet/idbDelete reuses one
// IDBDatabase instead of opening (and leaking) a fresh connection per call.
let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
    if (dbPromise) return dbPromise;
    dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
        if (typeof indexedDB === 'undefined') {
            reject(new Error('indexeddb-unavailable'));
            return;
        }
        const req = indexedDB.open(DB_NAME, DB_VERSION);
        req.onupgradeneeded = () => {
            const db = req.result;
            if (!db.objectStoreNames.contains(STORE)) {
                db.createObjectStore(STORE);
            }
        };
        req.onsuccess = () => {
            const db = req.result;
            // Drop the cache if the connection is closed or superseded so the
            // next call transparently reopens.
            db.onclose = () => {
                dbPromise = null;
            };
            db.onversionchange = () => {
                try {
                    db.close();
                } catch (_e) {
                    /* */
                }
                dbPromise = null;
            };
            resolve(db);
        };
        req.onerror = () => reject(req.error || new Error('idb-open-failed'));
    }).catch(err => {
        dbPromise = null; // allow a retry after a failed open
        throw err;
    });
    return dbPromise;
}

async function idbGet<T>(key: string): Promise<T | null> {
    try {
        const db = await openDb();
        return await new Promise<T | null>((resolve, reject) => {
            const tx = db.transaction(STORE, 'readonly');
            const req = tx.objectStore(STORE).get(key);
            req.onsuccess = () => resolve((req.result as T | undefined) ?? null);
            req.onerror = () => reject(req.error);
        });
    } catch (_err) {
        return null;
    }
}

async function idbSet<T>(key: string, value: T): Promise<void> {
    try {
        const db = await openDb();
        await new Promise<void>((resolve, reject) => {
            const tx = db.transaction(STORE, 'readwrite');
            tx.objectStore(STORE).put(value, key);
            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error);
        });
    } catch (_err) {
        // Best-effort — private browsing modes can reject IDB writes.
    }
}

async function idbDelete(key: string): Promise<void> {
    try {
        const db = await openDb();
        await new Promise<void>((resolve, reject) => {
            const tx = db.transaction(STORE, 'readwrite');
            tx.objectStore(STORE).delete(key);
            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error);
        });
    } catch (_err) {
        /* best-effort */
    }
}

/** Tell IDB which user is currently logged in on the page. The SW reads
 *  this on notificationclick to detect payload-vs-active mismatches.
 *  Optional display_name is used by the SW push handler to prefix the
 *  notification body with "from account X" when multiple users have
 *  active push tokens on this device. */
export async function setActiveUser(userId: string, displayName?: string): Promise<void> {
    if (!userId) return;
    await idbSet(KEY_ACTIVE_USER, userId);
    await addKnownUser(userId, displayName);
}

export async function getActiveUser(): Promise<string | null> {
    return idbGet<string>(KEY_ACTIVE_USER);
}

export async function clearActiveUser(): Promise<void> {
    await idbDelete(KEY_ACTIVE_USER);
}

/** Last Web Push endpoint we successfully registered, keyed per user. iOS Safari
 *  silently drops the push subscription when a PWA is force-closed and does NOT
 *  reliably fire `pushsubscriptionchange`, so the page re-subscribes and gets a
 *  NEW endpoint. getSubscription() then returns null, so we cannot read the old
 *  endpoint from the browser. Persisting it here lets the page pass it as
 *  `previous_endpoint` on the next register, so the backend reaps the rotated
 *  (now-dead) DeviceToken row instead of accumulating duplicate iOS devices. */
const KEY_LAST_ENDPOINT_PREFIX = 'last_endpoint:';

export async function getLastEndpoint(userId: string): Promise<string | null> {
    if (!userId) return null;
    return idbGet<string>(KEY_LAST_ENDPOINT_PREFIX + userId);
}

export async function setLastEndpoint(userId: string, endpoint: string): Promise<void> {
    if (!userId || !endpoint) return;
    await idbSet(KEY_LAST_ENDPOINT_PREFIX + userId, endpoint);
}

/** Queue a pending navigation. Called by the service worker right before
 *  it openWindow's a new tab so the freshly-mounted React app can pick the
 *  target up after validateUser() resolves. */
export async function setPendingNavigation(
    target: string,
    userId: string | null,
    accountId: string | null = null
): Promise<void> {
    if (!target) return;
    const payload: PendingNavigation = {target, user_id: userId, account_id: accountId, ts: Date.now()};
    await idbSet(KEY_PENDING_NAV, payload);
}

/** Read and remove the pending navigation. Stale entries (older than 5 min)
 *  are dropped — protects against a queued click that never reached a
 *  matching React mount (e.g. user closed the tab before login). */
export async function consumePendingNavigation(): Promise<PendingNavigation | null> {
    const entry = await idbGet<PendingNavigation>(KEY_PENDING_NAV);
    if (!entry) return null;
    await idbDelete(KEY_PENDING_NAV);
    if (typeof entry.ts !== 'number' || Date.now() - entry.ts > 5 * 60 * 1000) return null;
    return entry;
}

/** Bookkeeping: keep a small list of users who have ever logged in on this
 *  device, so the admin-mismatch dialog can show a friendly label without
 *  hitting the backend before validateUser() runs.
 *  Preserves the existing display_name if a new entry without one is added
 *  (e.g. a logout that only knows the id). */
export async function addKnownUser(userId: string, displayName?: string): Promise<void> {
    if (!userId) return;
    const list = (await idbGet<KnownUser[]>(KEY_KNOWN_USERS)) || [];
    const idx = list.findIndex(u => u.id === userId);
    const existing = idx >= 0 ? list[idx] : undefined;
    const entry: KnownUser = {
        id: userId,
        last_seen: Date.now(),
        display_name: displayName ?? existing?.display_name
    };
    if (idx >= 0) list[idx] = entry;
    else list.push(entry);
    // Cap at 20 — admin impersonation could otherwise grow unbounded.
    list.sort((a, b) => b.last_seen - a.last_seen);
    await idbSet(KEY_KNOWN_USERS, list.slice(0, 20));
}

export async function listKnownUsers(): Promise<KnownUser[]> {
    return (await idbGet<KnownUser[]>(KEY_KNOWN_USERS)) || [];
}

/** Track which users currently have push opted-in on this device. Mirrors
 *  the `bluemate-push-opted-in:<userId>` localStorage keys so the SW (which
 *  cannot read localStorage) can decide whether to prefix notifications
 *  with an account label when more than one user is receiving pushes. */
export async function setUserOptedIn(userId: string, optedIn: boolean): Promise<void> {
    if (!userId) return;
    const list = (await idbGet<string[]>(KEY_OPTED_IN_USERS)) || [];
    const has = list.includes(userId);
    if (optedIn && !has) {
        list.push(userId);
        await idbSet(KEY_OPTED_IN_USERS, list);
    } else if (!optedIn && has) {
        await idbSet(
            KEY_OPTED_IN_USERS,
            list.filter(id => id !== userId)
        );
    }
}

export async function listOptedInUsers(): Promise<string[]> {
    return (await idbGet<string[]>(KEY_OPTED_IN_USERS)) || [];
}

export interface OptedInPair {
    user_id: string;
    account_id: string;
}

/** Mirror the per-(user, account) push opt-in to IDB so the service worker
 *  can re-register the rotated endpoint on `pushsubscriptionchange`. The SW
 *  cannot read localStorage (where the page keeps the opt-in flags) and the
 *  /v1/device-token/register endpoint REQUIRES an account_id, so without this
 *  mirror the SW has no account to register the new endpoint under and the
 *  re-subscribe silently fails. */
export async function addOptedInPair(userId: string, accountId: string): Promise<void> {
    if (!userId || !accountId) return;
    const list = (await idbGet<OptedInPair[]>(KEY_OPTED_IN_PAIRS)) || [];
    if (list.some(p => p.user_id === userId && p.account_id === accountId)) return;
    list.push({user_id: userId, account_id: accountId});
    await idbSet(KEY_OPTED_IN_PAIRS, list);
}

export async function removeOptedInPair(userId: string, accountId: string): Promise<void> {
    if (!userId || !accountId) return;
    const list = (await idbGet<OptedInPair[]>(KEY_OPTED_IN_PAIRS)) || [];
    const next = list.filter(p => !(p.user_id === userId && p.account_id === accountId));
    if (next.length !== list.length) await idbSet(KEY_OPTED_IN_PAIRS, next);
}

// Set by app.tsx on every STANDALONE launch. The SW reads it in
// pushsubscriptionchange: on a MOBILE device that never launched the
// installed app, an endpoint rotation must NOT re-register the browser
// subscription (mobile registrations are installed-app-only); with the flag
// present the rotating subscription belongs to the installed app and
// re-registers as before.
const STANDALONE_SEEN_KEY = 'standalone_seen';

export async function setStandaloneSeen(): Promise<void> {
    await idbSet(STANDALONE_SEEN_KEY, true);
}

export async function getStandaloneSeen(): Promise<boolean> {
    return (await idbGet<boolean>(STANDALONE_SEEN_KEY)) === true;
}

// Set by app.tsx on every load from a phone/tablet, decided from the hardware
// (pwaUtil.isTouchFirstDevice) rather than the user-agent. The SW cannot read
// touch points or media queries, so its pushsubscriptionchange gate sniffs the
// UA — which the browser's "desktop site" toggle rewrites. This flag carries
// the page's verdict across to the SW so a phone running in desktop mode is
// still held to the installed-app-only rule.
const MOBILE_DEVICE_SEEN_KEY = 'mobile_device_seen';

export async function setMobileDeviceSeen(): Promise<void> {
    await idbSet(MOBILE_DEVICE_SEEN_KEY, true);
}

export async function getMobileDeviceSeen(): Promise<boolean> {
    return (await idbGet<boolean>(MOBILE_DEVICE_SEEN_KEY)) === true;
}

export async function listOptedInPairs(): Promise<OptedInPair[]> {
    return (await idbGet<OptedInPair[]>(KEY_OPTED_IN_PAIRS)) || [];
}

// ── Per-view unread counters ───────────────────────────────────────────────
//
// Keyed by `<accountId>:<route>` where route is the URL pathname a push targets
// (e.g. "/notificaties", "/review-analytics", "/facturen/orders"). The menu
// shows a badge per route for the CURRENT account; the app-icon badge shows the
// total across everything.

export type UnreadCountMap = Record<string, number>;

function unreadKey(accountId: string, route: string): string {
    return `${accountId}:${route}`;
}

/** Atomically increment the counter for (account, route) in a single IDB
 *  transaction so concurrent push events can't lose an update. Called by the SW
 *  push handler. */
export async function incrementUnreadCount(accountId: string, route: string): Promise<void> {
    if (!accountId || !route) return;
    try {
        const db = await openDb();
        await new Promise<void>((resolve, reject) => {
            const tx = db.transaction(STORE, 'readwrite');
            const store = tx.objectStore(STORE);
            const getReq = store.get(KEY_UNREAD_COUNTS);
            getReq.onsuccess = () => {
                const map = (getReq.result as UnreadCountMap | undefined) || {};
                const key = unreadKey(accountId, route);
                map[key] = (map[key] || 0) + 1;
                store.put(map, KEY_UNREAD_COUNTS);
            };
            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error);
        });
    } catch (_err) {
        /* best-effort */
    }
}

/** Reset the counter for (account, route) to zero — called when the user opens
 *  the target view. */
export async function resetUnreadCount(accountId: string, route: string): Promise<void> {
    if (!accountId || !route) return;
    try {
        const db = await openDb();
        await new Promise<void>((resolve, reject) => {
            const tx = db.transaction(STORE, 'readwrite');
            const store = tx.objectStore(STORE);
            const getReq = store.get(KEY_UNREAD_COUNTS);
            getReq.onsuccess = () => {
                const map = (getReq.result as UnreadCountMap | undefined) || {};
                const key = unreadKey(accountId, route);
                if (map[key]) {
                    delete map[key];
                    store.put(map, KEY_UNREAD_COUNTS);
                }
            };
            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error);
        });
    } catch (_err) {
        /* best-effort */
    }
}

/** Per-route counts for one account: { "/notificaties": 3, ... }. Used by the
 *  menus to badge the current account's items. */
export async function getUnreadCountsForAccount(accountId: string): Promise<Record<string, number>> {
    if (!accountId) return {};
    const map = (await idbGet<UnreadCountMap>(KEY_UNREAD_COUNTS)) || {};
    const prefix = `${accountId}:`;
    const out: Record<string, number> = {};
    for (const [key, count] of Object.entries(map)) {
        if (count > 0 && key.startsWith(prefix)) out[key.slice(prefix.length)] = count;
    }
    return out;
}

/** Total unread across every account + route — drives the installed-PWA app
 *  icon badge. */
export async function getTotalUnreadCount(): Promise<number> {
    const map = (await idbGet<UnreadCountMap>(KEY_UNREAD_COUNTS)) || {};
    let total = 0;
    for (const count of Object.values(map)) total += count > 0 ? count : 0;
    return total;
}

// ── Notification tray "ring" counter ───────────────────────────────────────
//
// Monotonic per-device counter driving the tray cap. Each NEW push takes the
// next slot (counter % trayLimit); when the ring wraps, the reused slot's tag
// makes the OS REPLACE the oldest notification, so the tray holds at most
// trayLimit toasts on BOTH iOS and Android. This is the only cap mechanism iOS
// honors — Notification.close() cannot remove a notification already delivered
// to the iOS Notification Center. Persisted so the ring survives the SW being
// killed between pushes.
const KEY_TRAY_COUNTER = 'tray_counter';

/** Atomically take the next tray-ring slot: reads the persisted push counter,
 *  writes counter+1 in the SAME transaction (so concurrent push events can't
 *  hand out the same slot), and returns `counter % trayLimit`. When IDB is
 *  unavailable (private mode, quota, corrupted DB) it falls back to a RANDOM slot
 *  — NOT a fixed 0: a fixed slot would give every push the same tag and the OS
 *  would collapse the whole tray to one visible toast (worse than the pre-ring
 *  behavior, which stacked). A random slot keeps notifications on distinct tags
 *  so they still stack; the cap just isn't enforced without the counter. */
export async function nextTraySlot(trayLimit: number): Promise<number> {
    const limit = Number.isFinite(trayLimit) && trayLimit >= 1 ? Math.floor(trayLimit) : 1;
    try {
        const db = await openDb();
        return await new Promise<number>((resolve, reject) => {
            const tx = db.transaction(STORE, 'readwrite');
            const store = tx.objectStore(STORE);
            const getReq = store.get(KEY_TRAY_COUNTER);
            let slot = 0;
            getReq.onsuccess = () => {
                const current = typeof getReq.result === 'number' && getReq.result >= 0 ? getReq.result : 0;
                slot = current % limit;
                store.put(current + 1, KEY_TRAY_COUNTER);
            };
            tx.oncomplete = () => resolve(slot);
            tx.onerror = () => reject(tx.error);
        });
    } catch (_err) {
        // No persisted counter: pick a random slot so distinct pushes get
        // distinct tags and stack, instead of all sharing bluemate-tray-0 and
        // collapsing to a single visible notification.
        return Math.floor(Math.random() * limit);
    }
}
